import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropertyCard from '../components/PropertyCard';
import styles from './ListingsPage.module.css';
import SEO from '../components/SEO';

const API = '/api';

export default function ListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const type = searchParams.get('type') || 'all';
  const city = searchParams.get('city') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = Number(searchParams.get('page') || 1);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(5000000);
  const [minArea, setMinArea] = useState(0);
  const [rooms, setRooms] = useState('');
  const [searchInput, setSearchInput] = useState(city);


  const typeLabel = { sprzedaz: 'Sprzedaż nieruchomości', wynajem: 'Wynajem nieruchomości', nowe: 'Nowe budownictwo', all: 'Ogłoszenia nieruchomości' };
  const pageTitle = (city ? city + ' — ' : '') + (typeLabel[type] || 'Ogłoszenia');
  const pageDesc = `Przeglądaj ogłoszenia nieruchomości${city ? ' w '+city : ''} na Nieruchomy.pl. ${total} ofert${type !== 'all' ? ' — '+typeLabel[type] : ''}. Darmowo, bez reklam.`;

  const fetchListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, sort });
      if (type !== 'all') params.set('type', type);
      if (city) params.set('city', city);
      if (minPrice > 0) params.set('minPrice', minPrice);
      if (maxPrice < 5000000) params.set('maxPrice', maxPrice);
      if (minArea > 0) params.set('minArea', minArea);
      if (rooms) params.set('rooms', rooms);
      const res = await fetch(`${API}/listings?${params}`);
      if (!res.ok) throw new Error('Błąd pobierania danych');
      const data = await res.json();
      setListings(data.data);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [type, city, sort, page, minPrice, maxPrice, minArea, rooms]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const set = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val && val !== 'all') p.set(key, val); else p.delete(key);
    p.set('page', '1');
    setSearchParams(p);
  };

  const handleSearch = () => set('city', searchInput);

  return (
    <>
    <SEO title={pageTitle} description={pageDesc} url={`https://nieruchomy.pl/ogloszenia${type !== 'all' ? '?type='+type : ''}`} noindex={page > 1} />
    <div className={styles.layout}>
      {/* SIDEBAR */}
      <aside className={styles.sidebar}>
        <div className={styles.sideCard}>
          <h3>Wyszukaj</h3>
          <input
            className={styles.input}
            type="text"
            placeholder="Miasto lub dzielnica..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button className={styles.searchBtn} onClick={handleSearch}>Szukaj</button>
        </div>

        <div className={styles.sideCard}>
          <h3>Typ ogłoszenia</h3>
          {['all','sprzedaz','wynajem','nowe'].map(t => (
            <label key={t} className={styles.radioRow}>
              <input type="radio" name="type" value={t} checked={type === t} onChange={() => set('type', t)} />
              <span>{{ all: 'Wszystkie', sprzedaz: 'Sprzedaż', wynajem: 'Wynajem', nowe: 'Nowe' }[t]}</span>
            </label>
          ))}
        </div>

        <div className={styles.sideCard}>
          <h3>Cena (zł)</h3>
          <div className={styles.rangeRow}>
            <label>Min: <strong>{minPrice.toLocaleString('pl-PL')} zł</strong></label>
            <input type="range" min="0" max="5000000" step="50000" value={minPrice} onChange={e => setMinPrice(+e.target.value)} />
          </div>
          <div className={styles.rangeRow}>
            <label>Max: <strong>{maxPrice.toLocaleString('pl-PL')} zł</strong></label>
            <input type="range" min="0" max="5000000" step="50000" value={maxPrice} onChange={e => setMaxPrice(+e.target.value)} />
          </div>
        </div>

        <div className={styles.sideCard}>
          <h3>Powierzchnia</h3>
          <div className={styles.rangeRow}>
            <label>Min: <strong>{minArea} m²</strong></label>
            <input type="range" min="0" max="500" step="5" value={minArea} onChange={e => setMinArea(+e.target.value)} />
          </div>
        </div>

        <div className={styles.sideCard}>
          <h3>Liczba pokoi</h3>
          <select className={styles.input} value={rooms} onChange={e => setRooms(e.target.value)}>
            <option value="">Dowolna</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
          </select>
          <button className={styles.applyBtn} onClick={fetchListings}>Zastosuj filtry</button>
        </div>
      </aside>

      {/* MAIN */}
      <main>
        <div className={styles.header}>
          <p className={styles.count}>
            Znaleziono <strong>{total}</strong> {total === 1 ? 'ofertę' : total < 5 ? 'oferty' : 'ofert'}
          </p>
          <select className={styles.sortSelect} value={sort} onChange={e => set('sort', e.target.value)}>
            <option value="newest">Najnowsze</option>
            <option value="price_asc">Cena rosnąco</option>
            <option value="price_desc">Cena malejąco</option>
            <option value="area">Metraż malejąco</option>
          </select>
        </div>

        {loading && <div className={styles.status}>Ładowanie ofert...</div>}
        {error && <div className={styles.error}>{error}</div>}

        {!loading && !error && listings.length === 0 && (
          <div className={styles.status}>Brak ofert spełniających kryteria.</div>
        )}

        <div className={styles.grid}>
          {listings.map((l, i) => <PropertyCard key={l.id} listing={l} index={i} />)}
        </div>

        {pages > 1 && (
          <div className={styles.pagination}>
            <button className={styles.pageBtn} disabled={page <= 1} onClick={() => set('page', page - 1)}>‹</button>
            {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
              <button key={p} className={`${styles.pageBtn} ${p === page ? styles.active : ''}`} onClick={() => set('page', p)}>{p}</button>
            ))}
            <button className={styles.pageBtn} disabled={page >= pages} onClick={() => set('page', page + 1)}>›</button>
          </div>
        )}
      </main>
    </div>
    </>
  );
}
